/**
 * Central titelmotor som speglar Rank Math-mallarna från Elmarknad.se.
 *
 *  - Startsida:   %sitename% - %sitedesc%
 *  - Sidor/CPT:   %title% - %sitename%
 *  - Taxonomi:    %term% - %sitename%
 *  - Sök:         %search_query% - %sitename%
 *  - 404:         Sidan hittades inte - %sitename%
 */

export const SITE_NAME = "Elmarknad.se";
export const SITE_TAGLINE = "Sveriges största jämförelsetjänst inom el & energi";
export const TITLE_SEP = "|";

export const HOME_TITLE = `Jämför elavtal och elpriser ${TITLE_SEP} ${SITE_NAME}`;

export type TitleType =
  | "home"
  | "page"
  | "article"
  | "product"
  | "term"
  | "search"
  | "notfound"
  /** Titeln används exakt som den är (redan komplett med varumärke). */
  | "raw";

const MAX_TITLE_LENGTH = 60;
const BRAND_SUFFIX = ` ${TITLE_SEP} ${SITE_NAME}`;

/** Tar bort befintligt varumärkessuffix så vi aldrig dubblerar det. */
function stripBrand(value: string): string {
  return value
    .replace(/\s*[|｜–—-]\s*Elmarknad(\.se)?\s*$/i, "")
    .trim();
}

/** Småord som aldrig får avsluta en kortad titel ("Vad kostar det att | …"). */
const DANGLING_WORDS = new Set([
  "och", "att", "som", "på", "i", "för", "med", "till", "av", "om", "det", "den", "de",
  "en", "ett", "är", "vad", "hur", "när", "vilket", "vilken", "vilka", "men", "eller",
  "så", "vs", "från", "inför", "efter", "under", "mot", "nära", "din", "ditt", "dina",
  "sin", "sitt", "sina", "vi", "du", "man", "&", "–", "—", "-", "»", "|",
]);

/**
 * Kortar text till högst `max` tecken på ordgräns och tar bort hängande
 * småord och skiljetecken i slutet, så att titeln aldrig slutar mitt i en fras.
 */
export function truncateAtWord(text: string, max: number): string {
  const value = text.trim();
  if (value.length <= max) return value;
  const words = value.slice(0, max + 1).split(" ");
  words.pop(); // sista ordet är avklippt (eller tomt om vi råkade klippa på ett mellanslag)
  const bare = (w: string) => w.toLowerCase().replace(/[,.:;!?]+$/, "");
  while (words.length > 1 && DANGLING_WORDS.has(bare(words[words.length - 1] ?? ""))) words.pop();
  const result = words.join(" ").replace(/[\s,.:;–—»|-]+$/, "");
  return result || value.slice(0, max);
}

/**
 * Längsta hela inledande led (före ":", " – ", " » ", " | " eller till och
 * med "?"/"!") som ryms inom `max` tecken, eller null om inget ryms.
 */
export function cutAtClause(text: string, max: number, min = 15): string | null {
  let best: string | null = null;
  for (const m of text.matchAll(/[:?!](?=\s)|\s[–—»|]\s/g)) {
    const i = m.index ?? 0;
    const prefix = (m[0] === "?" || m[0] === "!" ? text.slice(0, i + 1) : text.slice(0, i)).trim();
    if (prefix.length >= min && prefix.length <= max && (!best || prefix.length > best.length)) best = prefix;
  }
  return best;
}

/**
 * Titelbas + varumärke inom MAX_TITLE_LENGTH. Varumärket läggs bara på när
 * det ryms – hellre en hel titel utan "| Elmarknad.se" än en avklippt fras.
 * Längre titlar kortas i första hand vid ett helt led, annars på ordgräns.
 */
function fitTitle(base: string): string {
  const budget = MAX_TITLE_LENGTH - BRAND_SUFFIX.length;
  if (base.length <= budget) return `${base}${BRAND_SUFFIX}`;
  if (base.length <= MAX_TITLE_LENGTH) return base;
  const short = cutAtClause(base, MAX_TITLE_LENGTH) ?? truncateAtWord(base, MAX_TITLE_LENGTH);
  return short.length <= budget ? `${short}${BRAND_SUFFIX}` : short;
}

/**
 * Bygger en sidtitel enligt mallen för sidtypen.
 * Alla sidtyper utom startsidan använder `%title% - Elmarknad.se`.
 */
export function buildTitle(base: string, type: TitleType = "page"): string {
  if (type === "home") return HOME_TITLE;
  if (type === "raw") return (base ?? "").trim() || HOME_TITLE;

  const raw = type === "notfound" ? "Sidan hittades inte" : stripBrand(base ?? "");
  if (!raw) return HOME_TITLE;
  if (type === "search") return fitTitle(`Sökresultat för ${raw}`);

  return fitTitle(raw);
}

/** Kapar en beskrivning till ~155 tecken på ordgräns (motsvarar %excerpt%). */
export function buildDescription(text: string | undefined, max = 155): string | undefined {
  const value = (text ?? "").replace(/\s+/g, " ").trim();
  if (!value) return undefined;
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.:;-]+$/, "")}…`;
}

/** "i elområde SE3", "i elområdena SE2 och SE3" eller "i alla fyra elområden". */
function areaPhrase(areas: readonly string[]): string {
  const list = [...new Set(areas.filter((a) => /^SE[1-4]$/.test(a)))].sort();
  if (list.length === 0) return "";
  if (list.length === 4) return " i alla fyra elområden (SE1–SE4)";
  if (list.length === 1) return ` i elområde ${list[0]}`;
  return ` i elområdena ${list.slice(0, -1).join(", ")} och ${list[list.length - 1]}`;
}

/**
 * Unik metabeskrivning för elnätsbolagens sidor (/elnat/<slug>), byggd av
 * bolagets namn och elområde(n) från grid_operators.
 */
export function buildGridCompanyDescription(companyName: string, areas: readonly string[] = []): string {
  const name = stripBrand(companyName ?? "").trim();
  return `${name} är elnätsbolag${areaPhrase(areas)}. Se nätavgifter och villkor och jämför elavtal – elhandlaren väljer du fritt.`;
}

/** Suffix för tjänste- och lokala tjänstesidor (offertmallen). */
export const OFFER_TITLE_SUFFIX = `» Jämför 4 offerter | ${SITE_NAME}`;

/**
 * Titelmall för elhandelsbolagens sidor:
 * `Bolag » Priser, Elavtal & Recension | Elmarknad.se`
 */
export function buildCompanyTitle(companyName: string): string {
  return `${stripBrand(companyName ?? "").trim()} » Priser, Elavtal & Recension | ${SITE_NAME}`;
}

/**
 * Titelmall för tjänstesidor och lokala tjänstesidor:
 * `Tjänst i kommun » Jämför 4 offerter | Elmarknad.se`
 * Används med titelmallen "raw" så den lämnas orörd.
 */
export function buildOfferTitle(subject: string): string {
  return `${stripBrand(subject ?? "").trim()} ${OFFER_TITLE_SUFFIX}`;
}
